import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import { purchaseOrdersAPI, aiAPI } from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { ShoppingCart, Plus, Trash2, Loader2, UserCheck, Sparkles } from 'lucide-react';

/**
 * Pull seed items for a job. Order of preference:
 *   1. job.imported_from_po.items  (for jobs imported from a client PO)
 *   2. first accepted quote, else first quote on the job
 */
const buildSeedItems = (job, quotes) => {
  const snapItems = job?.imported_from_po?.items || [];
  if (snapItems.length) {
    return snapItems.map((it) => {
      // AI cross-reference suggestions (added on import) override empty trade/price
      const importTrade = it.trade_type && it.trade_type !== 'General' ? it.trade_type : (it.suggested_trade_type || it.trade_type || 'General');
      const importPrice = parseFloat(it.unit_price) || parseFloat(it.suggested_unit_price) || 0;
      const usedSuggestion = (
        (it.suggested_trade_type && importTrade === it.suggested_trade_type && (it.trade_type === 'General' || !it.trade_type))
        || (it.suggested_unit_price && importPrice === parseFloat(it.suggested_unit_price) && !parseFloat(it.unit_price))
      );
      return {
        description: it.description || '',
        quantity: parseFloat(it.quantity) || 1,
        unit: it.unit || 'Item',
        unit_price: importPrice,
        trade_type: importTrade,
        assigned_contractor_id: '',
        included: true,
        _origin: 'po_snapshot',
        _ai_suggested: !!usedSuggestion,
        _suggested_trade_type: it.suggested_trade_type,
        _suggested_unit_price: it.suggested_unit_price,
        _suggested_source_count: it.suggested_source_count || 0,
        _ai_confidence: it.ai_confidence,
      };
    });
  }
  if (quotes?.length) {
    const q = quotes.find((x) => x.status === 'accepted') || quotes[0];
    const flat = [];
    (q.sections || []).forEach((s) => (s.items || []).forEach((it) => flat.push(it)));
    const items = flat.length ? flat : (q.items || []);
    return items.map((it) => ({
      description: it.description || '',
      quantity: parseFloat(it.quantity) || 1,
      unit: it.unit || 'Item',
      unit_price: parseFloat(it.unit_price) || 0,
      trade_type: it.trade_type || 'General',
      assigned_contractor_id: '',
      included: true,
      _origin: 'quote',
    }));
  }
  return [];
};

export const GeneratePoFromJobDialog = ({
  open,
  onOpenChange,
  job,
  quotes,
  contractors = [],
  tradeTypes = [],
  jobId,
  onSuccess,
}) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [defaultContractorId, setDefaultContractorId] = useState('');
  const [enriching, setEnriching] = useState(false);

  useEffect(() => {
    if (!open) return;
    setItems(buildSeedItems(job, quotes));
    setDefaultContractorId('');
  }, [open, job, quotes]);

  const applyAiSuggestions = async () => {
    const payload = items
      .filter((it) => it.description)
      .map((it) => ({
        description: it.description,
        quantity: parseFloat(it.quantity) || 1,
        unit: it.unit || 'Item',
        unit_price: parseFloat(it.unit_price) || 0,
        trade_type: it.trade_type || null,
      }));
    if (payload.length === 0) {
      toast.error('Add at least one line item first');
      return;
    }
    setEnriching(true);
    try {
      const res = await aiAPI.enrichPoItems(payload);
      const enriched = res.data?.items || [];
      let updated = 0;
      setItems((prev) => prev.map((it, idx) => {
        const out = enriched[idx];
        if (!out) return it;
        const next = { ...it };
        if (out.suggested_trade_type && (!next.trade_type || next.trade_type === 'General')) {
          next.trade_type = out.suggested_trade_type;
          updated += 1;
        }
        if (out.suggested_unit_price && !parseFloat(next.unit_price)) {
          next.unit_price = out.suggested_unit_price;
          updated += 1;
        }
        next._ai_suggested = !!(out.suggested_trade_type || out.suggested_unit_price);
        next._suggested_trade_type = out.suggested_trade_type;
        next._suggested_unit_price = out.suggested_unit_price;
        next._suggested_source_count = out.suggested_source_count || 0;
        next._ai_confidence = out.ai_confidence;
        return next;
      }));
      toast.success(`AI suggestions applied${updated ? ` — ${updated} field(s) filled` : ' — no gaps found'}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fetch AI suggestions');
    } finally {
      setEnriching(false);
    }
  };

  const updateItem = (idx, field, value) => {
    setItems((prev) => {
      const next = [...prev];
      const numeric = field === 'quantity' || field === 'unit_price';
      next[idx] = { ...next[idx], [field]: numeric ? (parseFloat(value) || 0) : value };
      return next;
    });
  };

  const removeRow = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));

  const addRow = () =>
    setItems((prev) => [
      ...prev,
      { description: '', quantity: 1, unit: 'Item', unit_price: 0, trade_type: 'General', assigned_contractor_id: defaultContractorId, included: true, _origin: 'custom' },
    ]);

  const applyContractorToAll = (cid) => {
    if (!cid) return;
    setItems((prev) => prev.map((it) => ({ ...it, assigned_contractor_id: it.assigned_contractor_id || cid })));
    setDefaultContractorId(cid);
  };

  // Group included rows by contractor → preview totals
  const grouped = useMemo(() => {
    const map = {};
    items
      .filter((it) => it.included && it.description && it.assigned_contractor_id)
      .forEach((it) => {
        const cid = it.assigned_contractor_id;
        if (!map[cid]) {
          const c = contractors.find((x) => x.id === cid);
          map[cid] = {
            contractor_id: cid,
            contractor_name: c?.name || 'Unknown contractor',
            items: [],
            subtotal: 0,
          };
        }
        const lineTotal = (parseFloat(it.quantity) || 0) * (parseFloat(it.unit_price) || 0);
        map[cid].items.push(it);
        map[cid].subtotal += lineTotal;
      });
    return Object.values(map);
  }, [items, contractors]);

  // Items that are included but not assigned yet
  const unassignedCount = items.filter((it) => it.included && it.description && !it.assigned_contractor_id).length;

  const grandTotal = grouped.reduce((s, g) => s + g.subtotal * 1.1, 0);

  const submit = async () => {
    if (grouped.length === 0) {
      toast.error('Assign at least one item to a contractor');
      return;
    }
    if (unassignedCount > 0) {
      const ok = window.confirm(`${unassignedCount} included line(s) are not assigned to a contractor and will be skipped. Continue?`);
      if (!ok) return;
    }
    try {
      setLoading(true);
      const pos = grouped.map((group) => ({
        job_id: jobId,
        contractor_id: group.contractor_id,
        contractor_name: group.contractor_name,
        items: group.items.map((it) => ({
          description: it.description,
          quantity: parseFloat(it.quantity) || 0,
          unit: it.unit || 'Item',
          unit_price: parseFloat(it.unit_price) || 0,
          trade_type: it.trade_type || 'General',
        })),
      }));
      const res = await purchaseOrdersAPI.createBulk(pos);
      const created = res.data?.created ?? pos.length;
      const requested = res.data?.requested ?? pos.length;
      if (created < requested) {
        toast.error(`Created ${created} of ${requested} POs — the rest failed`);
      } else {
        toast.success(`Created ${created} PO${created === 1 ? '' : 's'}`);
      }
      onOpenChange?.(false);
      onSuccess?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create POs');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="generate-po-from-job-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5" /> Generate Purchase Orders
          </DialogTitle>
          <DialogDescription>
            {job?.imported_from_po?.po_number
              ? `Lines seeded from client PO ${job.imported_from_po.po_number}. Edit price/qty and assign each line to a contractor.`
              : 'Lines seeded from the latest quote on this job. Edit price/qty and assign each line to a contractor.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Bulk-assign contractor */}
          <div className="flex flex-wrap items-center gap-3 p-3 rounded border border-border bg-secondary/30">
            <Label className="text-sm flex items-center gap-1.5"><UserCheck className="h-4 w-4" /> Default contractor:</Label>
            <div className="min-w-[240px]">
              <Select value={defaultContractorId} onValueChange={applyContractorToAll}>
                <SelectTrigger data-testid="po-default-contractor"><SelectValue placeholder="Pick to auto-assign unassigned rows" /></SelectTrigger>
                <SelectContent>
                  {contractors.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}{c.trade_type ? ` · ${c.trade_type}` : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <span className="text-xs text-muted-foreground ml-auto">
              {items.length} source line{items.length === 1 ? '' : 's'} · {unassignedCount} unassigned
            </span>
            <Button size="sm" variant="outline" onClick={applyAiSuggestions} disabled={enriching} data-testid="po-apply-ai-suggestions">
              {enriching ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Sparkles className="h-3 w-3 mr-1" />}
              AI suggest trades & prices
            </Button>
            <Button size="sm" variant="outline" onClick={addRow} data-testid="po-add-row"><Plus className="h-3 w-3 mr-1" /> Add line</Button>
          </div>

          {/* Items table */}
          {items.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground border border-dashed border-border rounded-sm">
              No source items on this job. Click "Add line" to start, or create a quote first.
            </div>
          ) : (
            <div className="border border-border rounded">
              <div className="grid grid-cols-12 gap-2 px-3 py-2 text-xs uppercase tracking-wide text-muted-foreground bg-secondary/30">
                <div className="col-span-1">Use</div>
                <div className="col-span-4">Description</div>
                <div className="col-span-2">Trade</div>
                <div className="col-span-1">Qty</div>
                <div className="col-span-1">Unit</div>
                <div className="col-span-1">Price</div>
                <div className="col-span-2">Assign to contractor</div>
              </div>
              <div className="divide-y divide-border">
                {items.map((it, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-center px-3 py-2" data-testid={`po-row-${idx}`}>
                    <div className="col-span-1 flex items-center gap-1">
                      <input
                        type="checkbox"
                        checked={!!it.included}
                        onChange={(e) => updateItem(idx, 'included', e.target.checked)}
                        className="h-4 w-4 accent-primary"
                        data-testid={`po-row-included-${idx}`}
                      />
                      <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => removeRow(idx)} title="Remove line">
                        <Trash2 className="h-3 w-3 text-destructive" />
                      </Button>
                    </div>
                    <div className="col-span-4">
                      <Input value={it.description} onChange={(e) => updateItem(idx, 'description', e.target.value)} className="text-sm" placeholder="Description" data-testid={`po-row-description-${idx}`} />
                      {it._ai_suggested && (
                        <span
                          className="inline-flex items-center gap-1 mt-1 text-[10px] uppercase tracking-wider text-violet-400"
                          title={`AI suggestion${it._suggested_source_count ? ` from ${it._suggested_source_count} similar past line(s)` : (it._ai_confidence ? ` (confidence: ${it._ai_confidence})` : '')}`}
                          data-testid={`po-row-ai-badge-${idx}`}
                        >
                          <Sparkles className="h-2.5 w-2.5" /> AI suggested
                        </span>
                      )}
                    </div>
                    <div className="col-span-2">
                      <Select value={it.trade_type || 'General'} onValueChange={(v) => updateItem(idx, 'trade_type', v)}>
                        <SelectTrigger className="text-sm h-9" data-testid={`po-row-trade-${idx}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {(tradeTypes || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                          {!tradeTypes?.includes(it.trade_type || 'General') && it.trade_type && (
                            <SelectItem value={it.trade_type}>{it.trade_type}</SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-1">
                      <Input type="number" step="0.01" value={it.quantity} onChange={(e) => updateItem(idx, 'quantity', e.target.value)} className="text-sm" data-testid={`po-row-qty-${idx}`} />
                    </div>
                    <div className="col-span-1">
                      <Input value={it.unit} onChange={(e) => updateItem(idx, 'unit', e.target.value)} className="text-sm" />
                    </div>
                    <div className="col-span-1">
                      <Input type="number" step="0.01" value={it.unit_price} onChange={(e) => updateItem(idx, 'unit_price', e.target.value)} className="text-sm" data-testid={`po-row-price-${idx}`} />
                    </div>
                    <div className="col-span-2">
                      <Select value={it.assigned_contractor_id || ''} onValueChange={(v) => updateItem(idx, 'assigned_contractor_id', v)}>
                        <SelectTrigger className="text-sm h-9" data-testid={`po-row-contractor-${idx}`}>
                          <SelectValue placeholder="— pick —" />
                        </SelectTrigger>
                        <SelectContent>
                          {contractors.map((c) => (
                            <SelectItem key={c.id} value={c.id}>
                              {c.name}{c.trade_type ? ` · ${c.trade_type}` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Preview totals per contractor */}
          {grouped.length > 0 && (
            <div className="space-y-2" data-testid="po-preview">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">POs about to be created</div>
              {grouped.map((g) => (
                <div key={g.contractor_id} className="flex items-center justify-between px-3 py-2 rounded border border-border bg-secondary/20 text-sm" data-testid={`po-preview-${g.contractor_id}`}>
                  <div>
                    <div className="font-medium">{g.contractor_name}</div>
                    <div className="text-xs text-muted-foreground">{g.items.length} line(s)</div>
                  </div>
                  <div className="text-right font-mono">
                    <div className="font-bold">${(g.subtotal * 1.1).toFixed(2)}</div>
                    <div className="text-[11px] text-muted-foreground">${g.subtotal.toFixed(2)} + GST</div>
                  </div>
                </div>
              ))}
              <div className="flex justify-end text-sm font-bold font-mono pt-1 border-t border-border">
                Total: ${grandTotal.toFixed(2)}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange?.(false)} disabled={loading}>Cancel</Button>
          <Button onClick={submit} disabled={loading || grouped.length === 0} data-testid="po-create-submit">
            {loading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <ShoppingCart className="h-4 w-4 mr-1" />}
            {grouped.length === 0 ? 'Create POs' : `Create ${grouped.length} PO${grouped.length === 1 ? '' : 's'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default GeneratePoFromJobDialog;
